<template>
  <div class="employee-page">
    <section class="employee-profile">
      <div class="employee-avatar"><v-icon size="42">mdi-account</v-icon></div>
      <div class="employee-identity">
        <div class="d-flex align-start flex-wrap">
          <div>
            <h1>{{ employeeFullName }}</h1>
            <p class="employee-subtitle">{{ employee.companyName || translate('Сотрудник', 'employee') }}</p>
          </div>
          <div class="employee-number">
            {{ translate('Табельный номер', 'badgeNumber') }}:
            <strong>{{ employee.badgeNumber || employee.identNumber || '—' }}</strong>
          </div>
        </div>
        <div class="employee-meta">
          <span v-if="companyName"><v-icon small>mdi-domain</v-icon>{{ companyName }}</span>
          <span class="meta-chip meta-chip--accent">
            {{ translate('Категория', 'category') }}: {{ categoryName || '—' }}
          </span>
          <span class="meta-chip">
            {{ translate('Склад', 'distributionPoint') }}: {{ distributionPointName }}
          </span>
        </div>
      </div>
      <v-btn small outlined color="primary" class="edit-employee" @click="openDialog">
        <v-icon left small>mdi-pencil</v-icon>
        {{ translate('Редактировать', 'edit_button') }}
      </v-btn>
    </section>

    <div class="employee-workspace">
      <main class="kit-card">
        <div class="kit-toolbar">
          <div class="kit-filters">
            <v-btn-toggle v-model="kitFilter" mandatory dense borderless>
              <v-btn value="all" small>{{ translate('Все позиции', 'all_positions') }}</v-btn>
              <v-btn value="issue" small>{{ translate('Только к выдаче', 'only_to_issue') }}</v-btn>
              <v-btn value="replace" small>{{ translate('Только к замене', 'only_to_replace') }}</v-btn>
            </v-btn-toggle>
          </div>
          <v-checkbox
              v-model="hideComplete"
              dense
              hide-details
              class="mt-0"
              :label="translate('Скрыть полностью выданное', 'hide_issued')"
          ></v-checkbox>
        </div>

        <div class="kit-table-wrap">
          <table class="kit-table">
            <thead>
              <tr>
                <th class="kit-check">
                  <v-checkbox
                      :input-value="allVisibleSelected"
                      dense
                      hide-details
                      class="mt-0"
                      @change="toggleAllVisible"
                  ></v-checkbox>
                </th>
                <th>{{ translate('СИЗ', 'ppe') }}</th>
                <th>{{ translate('Норма', 'countNorm') }}</th>
                <th>{{ translate('Выдано', 'assignedCount') }}</th>
                <th>{{ translate('К выдаче', 'to_issue') }}</th>
                <th>{{ translate('Срок носки', 'wear_period') }}</th>
                <th>{{ translate('Статус', 'status') }}</th>
                <th>{{ translate('Выданные позиции', 'assignedAssets') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in visibleProvisionRows" :key="item.id">
                <td class="kit-check">
                  <v-checkbox
                      :input-value="selectedProvisionIds.includes(item.id)"
                      dense
                      hide-details
                      class="mt-0"
                      @change="toggleProvision(item.id)"
                  ></v-checkbox>
                </td>
                <td>
                  <div class="ppe-name">
                    <v-icon color="#0f8b8d">{{ item.icon }}</v-icon>
                    <span>{{ item.name }}</span>
                  </div>
                </td>
                <td>{{ item.required }} {{ item.unit }}</td>
                <td>{{ item.assigned }} {{ item.unit }}</td>
                <td><span class="issue-count">{{ item.toIssue }}</span></td>
                <td>{{ item.wearPeriod }}</td>
                <td>
                  <span class="status-badge" :class="`status-badge--${item.status}`">
                    <v-icon x-small>{{ statusIcon(item.status) }}</v-icon>
                    {{ statusText(item) }}
                  </span>
                </td>
                <td class="assigned-assets" :title="item.assignedAssets">{{ item.assignedAssets || '—' }}</td>
              </tr>
              <tr v-if="visibleProvisionRows.length === 0">
                <td colspan="8" class="empty-kit">
                  <v-icon>mdi-shield-check-outline</v-icon>
                  {{ translate('По выбранному фильтру позиций нет', 'no_filtered_positions') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="kit-note">
          <v-icon color="#1689a7">mdi-information-outline</v-icon>
          <span>{{ translate('Комплект сформирован автоматически на основании категории и нормативов сотрудника.', 'kit_auto_created') }}</span>
        </div>
        <div class="kit-footer">
          <div>
            <strong>{{ translate('Выбрано к выдаче', 'selected_to_issue') }}: {{ selectedIssueCount }}</strong>
            <span>{{ translate('ед.', 'units_short') }}</span>
          </div>
          <v-btn
              color="primary"
              depressed
              :disabled="selectedIssueCount === 0 || findingIssueAssets"
              :loading="findingIssueAssets"
              @click="prepareNormativeIssue"
          >
            {{ translate('Перейти к выдаче', 'go_to_issue') }}
            <v-icon right>mdi-arrow-down</v-icon>
          </v-btn>
        </div>
      </main>

      <aside class="employee-sidebar">
        <section class="sidebar-card">
          <h2>{{ translate('Сводка комплекта', 'kit_summary') }}</h2>
          <dl class="summary-list">
            <div><dt>{{ translate('Положено', 'required') }}:</dt><dd>{{ kitSummary.required }}</dd></div>
            <div><dt>{{ translate('Выдано', 'assignedCount') }}:</dt><dd class="text-success">{{ kitSummary.assigned }}</dd></div>
            <div><dt>{{ translate('К выдаче', 'to_issue') }}:</dt><dd>{{ kitSummary.toIssue }}</dd></div>
            <div><dt>{{ translate('Закрыто позиций', 'completed_positions') }}:</dt><dd>{{ kitSummary.completed }}</dd></div>
          </dl>
        </section>

        <section class="sidebar-card">
          <h2>{{ translate('Ближайшие действия', 'next_actions') }}</h2>
          <ul v-if="nextActions.length" class="action-list">
            <li v-for="item in nextActions" :key="item.id">
              <v-icon small color="error">mdi-alert-circle-outline</v-icon>
              <span>{{ translate('Выдать', 'issue') }}: {{ item.name }} ({{ item.toIssue }})</span>
            </li>
          </ul>
          <div v-else class="all-complete">
            <v-icon color="success">mdi-check-circle</v-icon>
            {{ translate('Комплект выдан полностью', 'kit_complete') }}
          </div>
        </section>

        <section class="sidebar-card employee-contacts">
          <h2>{{ translate('Контакты', 'contacts') }}</h2>
          <p><v-icon small>mdi-phone</v-icon>{{ employee.mobileNumber || employee.workNumber || '—' }}</p>
          <p><v-icon small>mdi-email-outline</v-icon>{{ employee.email || '—' }}</p>
        </section>
      </aside>
    </div>

    <section ref="issueCard" class="issue-card">
      <div class="issue-copy">
        <span class="section-kicker">{{ translate('Выдача', 'issue') }}</span>
        <h2>{{ translate('Найти СИЗ по инвентарному номеру', 'find_asset_to_issue') }}</h2>
        <p>{{ translate('Выберите найденную позицию и доступное действие.', 'select_asset_action_hint') }}</p>
      </div>
      <div class="issue-search">
        <v-text-field
            ref="issueSearch"
            v-model="searchedAssetId"
            :label="translate('Инвентарный номер СИЗ', 'Asset_Serial_Number')"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            clearable
            hide-details
            :error-messages="assetInputError"
            @click:clear="clearAssetList"
        ></v-text-field>
      </div>
      <v-expand-transition>
        <div v-if="dataList.length > 0" class="search-results-inline">
          <sm-person-data-iterator
              :key="$route.params.id"
              entity="Employee"
              :employee-id="Number($route.params.id)"
          ></sm-person-data-iterator>
        </div>
      </v-expand-transition>
    </section>

    <div class="employee-details-grid">
      <section class="detail-card assigned-card">
        <div class="detail-card-header">
          <div>
            <span class="section-kicker">{{ translate('На руках', 'on_hands') }}</span>
            <h2>{{ translate('Назначенные активы на текущий момент', 'Currently_assigned_assets') }}</h2>
          </div>
          <span class="detail-count">{{ assignedAssets.length }}</span>
        </div>
        <div v-if="assignedAssets.length" class="assigned-grid">
          <article
              v-for="asset in assignedAssets"
              :key="asset.id"
              class="assigned-item"
              :class="{'assigned-item--overdue': assignedAssetMeta(asset).overdue}"
          >
            <div class="assigned-icon">
              <v-icon>{{ Number(asset.identNumber) > 500000 ? 'mdi-shield-check-outline' : 'mdi-hammer-wrench' }}</v-icon>
            </div>
            <div class="assigned-copy">
              <strong>{{ assetModelName(asset) }}</strong>
              <span>№ {{ asset.identNumber || '—' }}</span>
              <div v-if="assignedAssetMeta(asset).expiryDate" class="assigned-expiry">
                <v-icon x-small>{{ assignedAssetMeta(asset).overdue ? 'mdi-alert-circle' : 'mdi-calendar-clock' }}</v-icon>
                {{ assignedAssetMeta(asset).label }}
              </div>
            </div>
          </article>
        </div>
        <div v-else class="detail-empty">
          <v-icon>mdi-package-variant-closed</v-icon>
          {{ translate('У сотрудника нет назначенных активов', 'no_assigned_assets') }}
        </div>
      </section>

      <section class="detail-card discipline-card">
        <div class="detail-card-header">
          <div>
            <span class="section-kicker">{{ translate('Контроль', 'control') }}</span>
            <h2>{{ translate('Дисциплина возвратов', 'emp_discipline') }}</h2>
          </div>
        </div>
        <div class="discipline-score">
          <strong>{{ rating.toFixed(1) }}</strong><span>/ 5</span>
          <v-rating color="primary" background-color="blue lighten-4" half-increments readonly dense :value="rating"></v-rating>
        </div>
        <div class="discipline-stats">
          <div><span>{{ translate('Выдано', 'assigned') }}</span><strong>{{ disciplineTotals.assigned }}</strong></div>
          <div><span>{{ translate('Возвращено', 'returned') }}</span><strong>{{ disciplineTotals.returned }}</strong></div>
          <div class="stat-danger"><span>{{ translate('Просрочено', 'overdue') }}</span><strong>{{ disciplineTotals.overdue }}</strong></div>
          <div class="stat-danger"><span>{{ translate('Утеряно', 'lost') }}</span><strong>{{ disciplineTotals.lost }}</strong></div>
        </div>
      </section>

      <section class="detail-card history-card">
        <div class="detail-card-header">
          <div>
            <span class="section-kicker">{{ translate('Хронология', 'timeline') }}</span>
            <h2>{{ translate('История выдачи и возвратов', 'hist_toolbar') }}</h2>
          </div>
          <span v-if="overdueHistoryCount" class="overdue-counter">
            <v-icon x-small>mdi-alert-circle</v-icon>
            {{ overdueHistoryCount }} {{ translate('просрочено', 'overdue') }}
          </span>
        </div>
        <div class="history-table-wrap">
          <table class="history-table">
            <thead>
              <tr>
                <th>{{ translate('Событие', 'eventName') }}</th>
                <th>{{ translate('Актив', 'asset') }}</th>
                <th>{{ translate('Дата выдачи', 'assetIssueDate') }}</th>
                <th>{{ translate('Выдано до', 'assetExpiryDate') }}</th>
                <th>{{ translate('Дата возврата', 'assetReturnedDate') }}</th>
                <th class="history-term-column">{{ translate('Использование срока', 'term_usage') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                  v-for="item in historyRows"
                  :key="item.id"
                  :class="{'history-row--overdue': item.isOverdue}"
              >
                <td><span class="event-pill" :class="`event-pill--${item.eventTone}`">{{ item.eventName }}</span></td>
                <td><strong>{{ item.assetName }}</strong><span class="history-ident">№ {{ item.identNumber }}</span></td>
                <td>{{ item.issueDate }}</td>
                <td>{{ item.expiryDate }}</td>
                <td>{{ item.returnDate }}</td>
                <td>
                  <div class="term-status" :class="{'term-status--overdue': item.isOverdue}">
                    <div class="term-status-label">
                      <span>{{ item.termLabel }}</span><strong>{{ item.progress }}%</strong>
                    </div>
                    <v-progress-linear
                        :value="item.progress"
                        :color="item.isOverdue ? 'error' : item.progress >= 80 ? 'warning' : 'success'"
                        background-color="blue-grey lighten-5"
                        height="7"
                        rounded
                    ></v-progress-linear>
                  </div>
                </td>
              </tr>
              <tr v-if="historyRows.length === 0"><td colspan="6" class="detail-empty">{{ translate('История пока пуста', 'empty_history') }}</td></tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <v-dialog v-model="issueDialog" persistent max-width="720px" scrollable>
      <v-card class="normative-issue-dialog">
        <v-card-title class="issue-dialog-title">
          <div>
            <span class="section-kicker">{{ translate('Выдача СИЗ', 'ppe_issue') }}</span>
            <h2>{{ translate('Подтверждение выдачи по нормативам', 'confirm_normative_issue') }}</h2>
          </div>
          <v-chip color="primary" outlined>{{ issueCandidates.length }} {{ translate('ед.', 'units_short') }}</v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="issue-dialog-content">
          <div class="issue-candidate-list">
            <div v-for="item in issueCandidates" :key="item.assetId" class="issue-candidate">
              <div class="issue-candidate-icon"><v-icon>mdi-shield-check-outline</v-icon></div>
              <div>
                <strong>{{ item.standardName }}</strong>
                <span>{{ item.modelName }} · № {{ item.identNumber }}</span>
              </div>
              <v-chip x-small color="success" outlined>{{ translate('На складе', 'in_storage') }}</v-chip>
            </div>
          </div>

          <v-alert v-if="unavailableStandards.length" type="warning" outlined dense class="mt-4 mb-0">
            {{ translate('Не найден свободный экземпляр', 'free_ppe_not_found') }}:
            {{ unavailableStandards.join(', ') }}.
          </v-alert>

          <div class="issue-return-date">
            <v-icon color="primary">mdi-calendar-clock</v-icon>
            <v-text-field
                v-model="issueReturnDate"
                type="date"
                outlined
                dense
                hide-details
                :min="todayDate"
                :label="translate('Дата возврата', 'assetExpiryDate')"
            ></v-text-field>
            <span>{{ translate('По умолчанию — один год с даты выдачи', 'default_issue_term_year') }}</span>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-5 py-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="issuingSelectedAssets" @click="closeIssueDialog">
            {{ translate('Отмена', 'cancel') }}
          </v-btn>
          <v-btn
              color="primary"
              depressed
              :loading="issuingSelectedAssets"
              :disabled="!issueReturnDate || issueCandidates.length === 0"
              @click="issueSelectedPpe"
          >
            {{ translate('Выдать выбранное', 'issue_selected') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
let getApi = () => import('@/services/Api');

export default {
  name: 'employee-page',
  components: {
    'sm-person-data-iterator': () => import('@/components/BasePersonDataIterator'),
  },
  data: () => ({
    employee: {},
    searchedAssetId: '',
    assetInputError: null,
    assetSearchDebounceId: null,
    kitFilter: 'all',
    hideComplete: false,
    selectedProvisionIds: [],
    issuedHistory: [],
    assignedAssets: [],
    ipmStandards: [],
    ppeModels: [],
    findingIssueAssets: false,
    issuingSelectedAssets: false,
    issueDialog: false,
    issueReturnDate: '',
    issueCandidates: [],
    unavailableStandards: [],
  }),
  computed: {
    dialogReadyCounter() { return this.$store.getters.getDialogReadyCounter; },
    refreshPageCounter() { return this.$store.getters.getPageKey; },
    dataList() { return [...this.$store.getters.getDtiItemList]; },
    todayDate() {
      const today = new Date();
      return this.toDateInputValue(today);
    },
    employeeFullName() {
      return [this.employee.lname, this.employee.fname].filter(Boolean).join(' ') || '—';
    },
    companyName() { return this.referenceValue(this.employee.company); },
    categoryName() { return this.referenceValue(this.employee.category); },
    distributionPointName() {
      const point = this.$store.getters.getAppDistributionPoint || this.$store.getters.getDistributionPoint;
      return this.referenceValue(point) || '—';
    },
    positionStandards() {
      return this.ipmStandards.map(row => {
        const type = row.type || {};
        return {
          id: row.id,
          type,
          typeId: Number(type.id),
          name: type.name || type,
          quantity: Number(row.count) || 1,
          unit: row.unit || 'шт.',
          wearPeriodMonths: Number(row.wearPeriodMonths) || 12,
          icon: this.standardIcon(type.code),
          modelIds: this.ppeModels.filter(model => Number((model.type || {}).id) === Number(type.id)).map(model => Number(model.id)),
        };
      });
    },
    activeIssuedAssets() {
      const latestByAsset = new Map();
      this.issuedHistory.forEach(item => {
        const asset = item.asset || {};
        if (!asset.id) return;
        const current = latestByAsset.get(asset.id);
        if (!current || new Date(item.createdAt) > new Date(current.createdAt)) latestByAsset.set(asset.id, item);
      });
      return Array.from(latestByAsset.values())
        .filter(item => ['ASSIGN', 'REASSIGN'].includes(((item.event || {}).code || '').toUpperCase()))
        .filter(item => Number((item.asset || {}).identNumber) > 500000)
        .map(item => {
          const model = ((item.asset || {}).model || {});
          const catalogModel = this.ppeModels.find(row => Number(row.id) === Number(model.id) || String(row.code) === String(model.code));
          return {
            id: item.asset.id,
            identNumber: item.asset.identNumber,
            modelCode: String(model.code || '').trim(),
            modelTypeId: Number(model.typeId || ((catalogModel || {}).type || {}).id),
            issueDate: item.assetIssueDate,
            expiryDate: item.assetExpiryDate,
          };
        });
    },
    provisionRows() {
      const lang = (this.$store.getters.getChosenLang || 'rus').toLowerCase();
      const usedAssetIds = new Set();
      const rows = this.positionStandards.map(standard => {
        const assets = this.activeIssuedAssets.filter(asset => !usedAssetIds.has(asset.id) && this.matchesStandard(standard, asset));
        assets.forEach(asset => usedAssetIds.add(asset.id));
        const required = Number(standard.quantity) || 0;
        const assigned = assets.length;
        const replacementAssets = assets.filter(asset => this.daysUntil(asset.expiryDate) <= 60);
        const toIssue = Math.max(required - assigned, 0) + replacementAssets.length;
        const replacementDays = replacementAssets.length
          ? Math.min(...replacementAssets.map(asset => this.daysUntil(asset.expiryDate)))
          : null;
        let status = 'issued';
        if (replacementAssets.length) status = 'replace';
        else if (toIssue > 0) status = assigned > 0 ? 'partial' : 'required';
        return {
          id: standard.id,
          name: (standard.name || {})[lang] || (standard.name || {}).rus || standard.id,
          required,
          assigned,
          toIssue,
          replacementDays,
          assignedAssets: assets.map(asset => `${asset.identNumber} — ${asset.modelCode || 'СИЗ'}`).join('; '),
          status,
          unit: typeof standard.unit === 'string' ? standard.unit : (standard.unit || {})[lang] || (standard.unit || {}).rus || 'шт.',
          wearPeriod: this.formatWearPeriod(standard.wearPeriodMonths),
          icon: standard.icon || 'mdi-shield-check-outline',
        };
      });
      this.activeIssuedAssets
        .filter(asset => !usedAssetIds.has(asset.id))
        .forEach(asset => rows.push({
          id: `unmatched-${asset.id}`,
          name: asset.modelCode || this.translate('СИЗ без модели', 'ppe_without_model'),
          required: 0,
          assigned: 1,
          toIssue: 0,
          replacementDays: null,
          assignedAssets: asset.identNumber,
          status: 'extra',
          unit: this.translate('шт.', 'units_short'),
          wearPeriod: '—',
          icon: 'mdi-shield-alert-outline',
        }));
      return rows;
    },
    visibleProvisionRows() {
      return this.provisionRows.filter(item => {
        if (this.hideComplete && item.toIssue === 0) return false;
        if (this.kitFilter === 'issue') return item.toIssue > 0;
        if (this.kitFilter === 'replace') return item.status === 'replace';
        return true;
      });
    },
    allVisibleSelected() {
      return this.visibleProvisionRows.length > 0 && this.visibleProvisionRows.every(item => this.selectedProvisionIds.includes(item.id));
    },
    kitSummary() {
      return this.provisionRows.reduce((summary, item) => {
        summary.required += item.required;
        summary.assigned += item.assigned;
        summary.toIssue += item.toIssue;
        if (item.required > 0 && item.status === 'issued') summary.completed += 1;
        return summary;
      }, {required: 0, assigned: 0, toIssue: 0, completed: 0});
    },
    selectedIssueCount() {
      return this.provisionRows
        .filter(item => this.selectedProvisionIds.includes(item.id))
        .reduce((total, item) => total + item.toIssue, 0);
    },
    nextActions() { return this.provisionRows.filter(item => item.toIssue > 0).slice(0, 4); },
    disciplineTotals() {
      const rows = this.$store.getters.getSdtRowsByEntity('EmployeeDiscipline');
      return rows.reduce((result, item) => {
        result.assigned += Number(item.assigned) || 0;
        result.lost += Number(item.lost) || 0;
        result.overdue += Number(item.overdue) || 0;
        result.returned += Number(item.returned) || 0;
        return result;
      }, {assigned: 0, returned: 0, lost: 0, overdue: 0});
    },
    rating() {
      const totals = this.disciplineTotals;
      if (!totals.assigned) return 5;
      return Math.max(0, (100 - (100 * (totals.lost + totals.overdue) / totals.assigned)) / 20);
    },
    latestHistoryIds() {
      const latest = new Map();
      this.issuedHistory.forEach(item => {
        const assetId = ((item.asset || {}).id);
        if (!assetId) return;
        const current = latest.get(assetId);
        if (!current || new Date(item.createdAt) > new Date(current.createdAt)) latest.set(assetId, item);
      });
      return new Set(Array.from(latest.values()).map(item => item.id));
    },
    historyRows() {
      return [...this.issuedHistory]
        .sort((first, second) => new Date(second.createdAt) - new Date(first.createdAt))
        .map(item => this.prepareHistoryRow(item));
    },
    overdueHistoryCount() {
      return this.historyRows.filter(item => item.isOverdue).length;
    },
  },
  watch: {
    dialogReadyCounter() { this.loadEmployeePage(); },
    refreshPageCounter() { this.loadEmployeePage(); },
    searchedAssetId(value) {
      if (value === undefined) return;
      this.queueAssetSearch(value);
    },
  },
  async created() {
    this.issueReturnDate = this.defaultIssueReturnDate();
    await this.$store.dispatch('storeTabHeader', {table: 'Employee'});
    await this.$store.dispatch('storeTabHeader', {table: 'Asset'});
    await this.loadEmployeePage();
    this.$store.dispatch('storeDtiItemList');
    document.querySelector('html').style.overflowY = 'auto';
  },
  beforeDestroy() { this.clearQueuedAssetSearch(); },
  methods: {
    translate(def, code, entity) { return this.$store.getters.getTranslation(def, code, entity); },
    referenceValue(reference) {
      if (!reference) return '';
      if (typeof reference === 'string') return reference;
      const lang = (this.$store.getters.getChosenLang || 'rus').toLowerCase();
      if (reference.name && typeof reference.name === 'object') {
        return reference.name[lang] || reference.name.rus || reference.name.value || '';
      }
      return reference[lang] || reference.rus || reference.value || reference.code || '';
    },
    matchesStandard(standard, asset) {
      return Number(standard.typeId) === Number(asset.modelTypeId);
    },
    standardIcon(code) {
      const icons = {PPE_HELMET: 'mdi-hard-hat', PPE_SUMMER_SUIT: 'mdi-tshirt-crew-outline', PPE_BOOTS: 'mdi-shoe-formal', PPE_GLOVES: 'mdi-hand-back-right-outline', PPE_GLASSES: 'mdi-safety-goggles', PPE_WINTER_CLOTHING: 'mdi-coat-rack', PPE_SAFETY_BELT: 'mdi-seatbelt'};
      return icons[String(code || '').toUpperCase()] || 'mdi-shield-check-outline';
    },
    daysUntil(date) {
      if (!date) return Number.POSITIVE_INFINITY;
      return Math.ceil((new Date(date).getTime() - Date.now()) / 86400000);
    },
    formatWearPeriod(months) {
      const value = Number(months) || 0;
      if (value && value % 12 === 0) {
        const years = value / 12;
        return `${years} ${years === 1 ? 'год' : years < 5 ? 'года' : 'лет'}`;
      }
      return value ? `${value} мес.` : '—';
    },
    async loadEmployeePage() {
      const employeeId = Number(this.$route.params.id);
      await Promise.all([this.getEmployeeInfo(employeeId), this.getPpeModels()]);
      await Promise.all([
        this.getIpmStandards(),
        this.getIssuedHistory(employeeId),
        this.getAssignedAssets(employeeId),
        this.$store.dispatch('storeSdtRows', {entity: 'EmployeeDiscipline', entityId: employeeId}),
      ]);
    },
    async getEmployeeInfo(employeeId) {
      const {http} = await getApi();
      const response = await http.getData('view/Employee/', {where: [{value: 'id', text: employeeId}]});
      if (response.data.tabRows && response.data.tabRows.length > 0) this.employee = response.data.tabRows[0];
    },
    async getIpmStandards() {
      const categoryId = Number((this.employee.category || {}).id);
      if (!categoryId) { this.ipmStandards = []; return; }
      const {http} = await getApi();
      const response = await http.getData('view/IpmStandards', {lang: (this.$store.getters.getChosenLang || 'rus').toLowerCase(), where: [{value: 'employeeCategoryId', text: categoryId}], sort: {descending: false, rowsPerPage: 200, page: 1}});
      this.ipmStandards = (response.data && response.data.tabRows) || [];
    },
    async getPpeModels() {
      const {http} = await getApi();
      const response = await http.getData('view/AssetModel', {lang: (this.$store.getters.getChosenLang || 'rus').toLowerCase(), sort: {descending: false, rowsPerPage: 500, page: 1}, where: []});
      this.ppeModels = ((response.data && response.data.tabRows) || []).filter(model => Number(model.code) > 500000);
    },
    async getIssuedHistory(employeeId) {
      const {http} = await getApi();
      const response = await http.getData('assetHistory', {
        employeeId,
        lang: (this.$store.getters.getChosenLang || 'rus').toLowerCase(),
        sort: {descending: false, rowsPerPage: -1, page: 1},
      });
      this.issuedHistory = (response.data && response.data.history) || [];
    },
    async getAssignedAssets(employeeId) {
      const {http} = await getApi();
      const response = await http.getData('view/Asset', {
        where: [{value: 'employeeId', text: employeeId}],
      });
      this.assignedAssets = (response.data && response.data.tabRows) || [];
    },
    assetModelName(asset) {
      const model = asset.model || {};
      return this.referenceValue(model.name) || model.value || model.code || this.translate('Актив', 'asset');
    },
    assignedAssetMeta(asset) {
      const activeHistory = this.activeIssuedAssets.find(item => item.id === asset.id);
      const expiryDate = (activeHistory || {}).expiryDate || asset.expiryDate || asset.nextCheckDate;
      if (!expiryDate) return {expiryDate: null, overdue: false, label: ''};
      const days = this.daysUntil(expiryDate);
      return {
        expiryDate,
        overdue: days < 0,
        label: days < 0
          ? `${this.translate('Просрочено на', 'overdue_by')} ${Math.abs(days)} ${this.translate('дн.', 'days_short')}`
          : `${this.translate('До', 'until')} ${this.formatDate(expiryDate)}`,
      };
    },
    prepareHistoryRow(item) {
      const event = item.event || {};
      const code = String(event.code || '').toUpperCase();
      const asset = item.asset || {};
      const model = asset.model || {};
      const issueTimestamp = item.assetIssueDate ? new Date(item.assetIssueDate).getTime() : null;
      const expiryTimestamp = item.assetExpiryDate ? new Date(item.assetExpiryDate).getTime() : null;
      const returnTimestamp = item.assetReturnedDate ? new Date(item.assetReturnedDate).getTime() : null;
      const isLatestAssignment = this.latestHistoryIds.has(item.id) && ['ASSIGN', 'REASSIGN'].includes(code);
      const endTimestamp = returnTimestamp || (isLatestAssignment ? Date.now() : null);
      const allowedDays = issueTimestamp && expiryTimestamp ? Math.max(1, Math.ceil((expiryTimestamp - issueTimestamp) / 86400000)) : 0;
      const usedDays = issueTimestamp && endTimestamp ? Math.max(0, Math.ceil((endTimestamp - issueTimestamp) / 86400000)) : 0;
      const calculatedOverdue = Boolean(expiryTimestamp && endTimestamp && endTimestamp > expiryTimestamp);
      const isOverdue = Number(item.overdue) > 0 || calculatedOverdue;
      const overdueDays = isOverdue && expiryTimestamp && endTimestamp
        ? Math.max(1, Math.ceil((endTimestamp - expiryTimestamp) / 86400000))
        : 0;
      let progress = allowedDays && endTimestamp ? Math.round((usedDays / allowedDays) * 100) : 0;
      progress = Math.max(0, Math.min(100, progress));
      let termLabel = this.translate('Срок не задан', 'term_not_set');
      if (isOverdue) termLabel = `${this.translate('Просрочено', 'overdue')} ${overdueDays} ${this.translate('дн.', 'days_short')}`;
      else if (returnTimestamp) termLabel = this.translate('Возвращено в срок', 'returned_on_time');
      else if (isLatestAssignment && expiryTimestamp) termLabel = `${Math.max(0, this.daysUntil(item.assetExpiryDate))} ${this.translate('дн. осталось', 'days_left')}`;
      else if (allowedDays) termLabel = this.translate('Завершённый период', 'completed_period');
      return {
        id: item.id,
        eventName: this.referenceValue(event.name) || this.translate(code || '—', `ACTION_${code}`),
        eventTone: ['ASSIGN', 'REASSIGN'].includes(code) ? 'issue' : code === 'RETURN' ? 'return' : 'neutral',
        assetName: model.code || this.translate('Актив', 'asset'),
        identNumber: asset.identNumber || '—',
        issueDate: this.formatDate(item.assetIssueDate, true),
        expiryDate: this.formatDate(item.assetExpiryDate, true),
        returnDate: this.formatDate(item.assetReturnedDate, true),
        isOverdue,
        progress,
        termLabel,
      };
    },
    formatDate(date, withTime = false) {
      if (!date) return '—';
      return this.$store.getters.formatDate(date, withTime ? 1 : 0) || '—';
    },
    toggleProvision(id) {
      const index = this.selectedProvisionIds.indexOf(id);
      if (index === -1) this.selectedProvisionIds.push(id);
      else this.selectedProvisionIds.splice(index, 1);
    },
    toggleAllVisible(selected) {
      const visibleIds = this.visibleProvisionRows.map(item => item.id);
      if (selected) this.selectedProvisionIds = Array.from(new Set([...this.selectedProvisionIds, ...visibleIds]));
      else this.selectedProvisionIds = this.selectedProvisionIds.filter(id => !visibleIds.includes(id));
    },
    toDateInputValue(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    defaultIssueReturnDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 1);
      return this.toDateInputValue(date);
    },
    selectedStandardsToIssue() {
      const selectedRows = new Map(this.provisionRows.map(row => [row.id, row]));
      return this.positionStandards.filter(standard => {
        const row = selectedRows.get(standard.id);
        return this.selectedProvisionIds.includes(standard.id) && row && row.toIssue > 0;
      });
    },
    async findAvailableAssetForStandard(standard) {
      const modelIds = (standard.modelIds || []).map(Number).filter(Number.isInteger);
      if (!modelIds.length) return null;
      const distributionPoint = this.$store.getters.getDistributionPoint || {};
      const where = [
        {value: 'modelId', text: modelIds, operator: 'in'},
        {value: 'statusId', text: 1},
        {value: 'identNumber', text: '500000', operator: '>'},
      ];
      if (distributionPoint.id) where.push({value: 'distributionPointId', text: distributionPoint.id});
      const {http} = await getApi();
      const response = await http.getData('view/Asset', {
        where,
        sort: {sortBy: 'identNumber', descending: false, rowsPerPage: 100, page: 1},
      });
      const rows = (response.data && response.data.tabRows) || [];
      const now = Date.now();
      return rows.find(asset => !asset.expiryDate || new Date(asset.expiryDate).getTime() >= now) || null;
    },
    async prepareNormativeIssue() {
      const standards = this.selectedStandardsToIssue();
      if (!standards.length) return;
      this.findingIssueAssets = true;
      this.issueCandidates = [];
      this.unavailableStandards = [];
      try {
        const assets = await Promise.all(standards.map(standard => this.findAvailableAssetForStandard(standard)));
        const reservedAssetIds = new Set();
        standards.forEach((standard, index) => {
          const asset = assets[index];
          const row = this.provisionRows.find(item => item.id === standard.id) || {};
          if (!asset || reservedAssetIds.has(asset.id)) {
            this.unavailableStandards.push(row.name || standard.id);
            return;
          }
          reservedAssetIds.add(asset.id);
          this.issueCandidates.push({
            standardId: standard.id,
            standardName: row.name || standard.id,
            assetId: asset.id,
            identNumber: asset.identNumber,
            modelName: this.assetModelName(asset),
          });
        });
        if (!this.issueCandidates.length) {
          this.$store.dispatch('storeError', this.translate('На текущем складе нет свободных СИЗ для выбранных нормативов.', 'selected_ppe_not_in_storage'));
          return;
        }
        this.issueReturnDate = this.defaultIssueReturnDate();
        this.issueDialog = true;
      } catch (error) {
        this.$store.dispatch('storeError', error);
      } finally {
        this.findingIssueAssets = false;
      }
    },
    closeIssueDialog() {
      if (this.issuingSelectedAssets) return;
      this.issueDialog = false;
      this.issueCandidates = [];
      this.unavailableStandards = [];
    },
    async issueSelectedPpe() {
      if (!this.issueReturnDate || !this.issueCandidates.length) return;
      const expiryDate = new Date(`${this.issueReturnDate}T23:59:59`);
      if (Number.isNaN(expiryDate.getTime()) || expiryDate.getTime() < Date.now()) {
        this.$store.dispatch('storeError', this.translate('Дата возврата должна быть не раньше сегодняшней даты.', 'invalid_asset_expiry_date'));
        return;
      }
      const distributionPoint = this.$store.getters.getDistributionPoint || {};
      if (!distributionPoint.id) {
        this.$store.dispatch('storeError', this.translate('Для пользователя не задан пункт выдачи.', 'distribution_point_required'));
        return;
      }
      this.issuingSelectedAssets = true;
      await this.$store.dispatch('storeProgress', {text: this.translate('Выдача СИЗ', 'ppe_issue')});
      try {
        const {http} = await getApi();
        const issuedStandardIds = [];
        for (const item of this.issueCandidates) {
          const response = await http.postData('/fsm/makeTransition', {
            nextTransition: 'assign',
            transitionData: {
              employeeId: Number(this.$route.params.id),
              assetId: item.assetId,
              assetIssueDate: new Date().toISOString(),
              issueDistPointId: distributionPoint.id,
              assetExpiryDate: expiryDate.toISOString(),
              comment: '',
            },
          });
          if (!response || response.status !== 200) break;
          issuedStandardIds.push(item.standardId);
        }
        if (issuedStandardIds.length) {
          this.selectedProvisionIds = this.selectedProvisionIds.filter(id => !issuedStandardIds.includes(id));
          this.issueDialog = false;
          this.issueCandidates = [];
          this.unavailableStandards = [];
          this.$store.dispatch('storeDtiItemList');
          await this.loadEmployeePage();
        }
      } catch (error) {
        this.$store.dispatch('storeError', error);
      } finally {
        this.issuingSelectedAssets = false;
        await this.$store.dispatch('storeProgress', {text: null});
      }
    },
    statusText(item) {
      if (item.status === 'issued') return this.translate('выдано', 'issued');
      if (item.status === 'replace') {
        if (item.replacementDays < 0) return this.translate('замена просрочена', 'replacement_overdue');
        return `${this.translate('замена через', 'replacement_in')} ${item.replacementDays} ${this.translate('дн.', 'days_short')}`;
      }
      if (item.status === 'extra') return this.translate('выдано вне норматива', 'issued_outside_standard');
      if (item.status === 'partial') return this.translate('выдано частично', 'partially_issued');
      return this.translate('требуется выдать', 'required_to_issue');
    },
    statusIcon(status) {
      if (status === 'issued') return 'mdi-check-circle';
      if (status === 'replace' || status === 'partial') return 'mdi-clock-outline';
      if (status === 'extra') return 'mdi-information-outline';
      return 'mdi-alert-circle';
    },
    clearQueuedAssetSearch() {
      if (this.assetSearchDebounceId) {
        clearTimeout(this.assetSearchDebounceId);
        this.assetSearchDebounceId = null;
      }
    },
    queueAssetSearch(value, delay = 300) {
      this.clearQueuedAssetSearch();
      this.assetSearchDebounceId = setTimeout(() => this.searchAsset(value), delay);
    },
    async searchAsset(value = this.searchedAssetId) {
      const searchable = typeof value === 'string' ? value.trim() : value;
      if (searchable) {
        this.assetInputError = null;
        this.$store.dispatch('storeDtiItemList', {identNumber: searchable});
      } else this.clearAssetList();
    },
    clearAssetList() {
      this.clearQueuedAssetSearch();
      this.$store.dispatch('storeDtiItemList');
    },
    async openDialog() {
      await this.$store.dispatch('storeProgress', {text: 'Processing'});
      await this.$store.dispatch('storeDialogParams', {action: 'edit', editedItemId: this.employee.id});
      await this.$store.dispatch('storeDialogTable', {table: 'Employee'});
      await this.$store.dispatch('storeProgress', {text: null});
      this.$store.dispatch('storeDialog', {val: true});
    },
  },
};
</script>

<style scoped>
.employee-page { padding: 16px; color: #17324d; }
.employee-profile, .kit-card, .sidebar-card, .issue-card, .detail-card {
  border: 1px solid #dce6ef; border-radius: 12px; background: #fff;
  box-shadow: 0 8px 24px rgba(30, 64, 96, .06);
}
.employee-profile { display: flex; align-items: center; gap: 18px; padding: 16px 20px; margin-bottom: 16px; }
.employee-avatar { display: grid; place-items: center; width: 68px; height: 68px; flex: 0 0 68px; border: 1px solid #cde0e8; border-radius: 50%; color: #07878a; background: #f1fbfb; }
.employee-avatar .v-icon { color: inherit; }
.employee-identity { flex: 1; min-width: 0; }
.employee-identity h1 { margin: 0; font-size: 21px; line-height: 1.25; }
.employee-subtitle { margin: 4px 0 0; color: #617386; }
.employee-number { margin: 4px 0 0 34px; color: #65778a; }
.employee-number strong { margin-left: 8px; color: #17324d; }
.employee-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.employee-meta > span { display: inline-flex; align-items: center; gap: 6px; }
.meta-chip { padding: 5px 12px; border: 1px solid #dce4ec; border-radius: 6px; background: #f5f7fa; }
.meta-chip--accent { border-color: #c7e8e7; color: #087d80; background: #edf9f8; }
.edit-employee { align-self: flex-start; }
.employee-workspace { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 16px; align-items: start; }
.kit-card { overflow: hidden; }
.kit-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 12px 14px; border-bottom: 1px solid #e5ebf1; }
.kit-filters .v-btn-toggle { border: 1px solid #d8e4eb; border-radius: 7px; overflow: hidden; }
.kit-filters .v-btn { text-transform: none; letter-spacing: 0; color: #617386; background: #fff !important; }
.kit-filters .v-btn--active { color: #087d80 !important; background: #eefafa !important; }
.kit-table-wrap { overflow-x: auto; }
.kit-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.kit-table th { padding: 11px 10px; color: #65778a; font-weight: 600; text-align: left; white-space: nowrap; background: #f7f9fb; }
.kit-table td { width: auto; max-width: 220px; padding: 10px; border-bottom: 1px solid #edf1f5 !important; color: #3f556b; text-align: left; white-space: nowrap !important; }
.kit-table tbody tr:hover { background: #fbfdfd; }
.kit-check { width: 40px !important; padding-left: 12px !important; padding-right: 4px !important; }
.ppe-name { display: flex; align-items: center; gap: 10px; min-width: 180px; color: #254057; font-weight: 500; }
.issue-count { display: inline-flex; min-width: 48px; justify-content: center; padding: 6px 12px; border: 1px solid #dfe6ec; border-radius: 6px; color: #233d54; background: #fff; }
.assigned-assets { max-width: 210px !important; overflow: hidden; text-overflow: ellipsis; }
.status-badge { display: inline-flex; align-items: center; gap: 5px; padding: 5px 9px; border-radius: 6px; font-weight: 600; }
.status-badge--issued { color: #07966e; background: #eafaf4; }
.status-badge--partial { color: #d68015; background: #fff7e8; }
.status-badge--replace { color: #d68015; background: #fff7e8; }
.status-badge--required { color: #e04d5d; background: #fff0f2; }
.status-badge--extra { color: #287a9b; background: #edf8fc; }
.status-badge .v-icon { color: inherit; }
.empty-kit { padding: 36px !important; color: #7b8c9d !important; text-align: center !important; }
.kit-note { display: flex; align-items: center; gap: 10px; margin: 12px; padding: 10px 12px; border: 1px solid #cce8f1; border-radius: 7px; color: #477087; background: #edf8fc; }
.kit-footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 0 12px 12px; }
.kit-footer strong { color: #17324d; }
.kit-footer span { margin-left: 5px; color: #64778a; }
.employee-sidebar { display: flex; flex-direction: column; gap: 12px; }
.sidebar-card { padding: 14px 16px; }
.sidebar-card h2 { margin: 0 0 12px; font-size: 15px; }
.summary-list { margin: 0; }
.summary-list div { display: flex; justify-content: space-between; padding: 4px 0; }
.summary-list dt { color: #64778a; }
.summary-list dd { margin: 0; color: #17324d; font-weight: 700; }
.summary-list .text-success { color: #07966e; }
.action-list { margin: 0; padding: 0; list-style: none; }
.action-list li { display: flex; gap: 8px; padding: 6px 0; color: #4a6075; }
.all-complete { display: flex; align-items: center; gap: 8px; color: #078766; }
.employee-contacts p { display: flex; align-items: center; gap: 8px; margin: 8px 0; color: #617386; }
.issue-card { display: grid; grid-template-columns: minmax(230px, .8fr) minmax(300px, 1.2fr); gap: 20px; align-items: center; margin-top: 16px; padding: 16px 18px; }
.section-kicker { color: #07878a; font-size: 11px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.issue-copy h2 { margin: 3px 0; font-size: 17px; }
.issue-copy p { margin: 0; color: #718396; font-size: 13px; }
.normative-issue-dialog { border-radius: 12px !important; }
.issue-dialog-title { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.issue-dialog-title h2 { margin: 3px 0 0; color: #213d55; font-size: 19px; }
.issue-dialog-content { padding: 20px 24px !important; }
.issue-candidate-list { display: flex; flex-direction: column; gap: 8px; }
.issue-candidate { display: grid; grid-template-columns: 42px minmax(0, 1fr) auto; gap: 12px; align-items: center; padding: 11px 12px; border: 1px solid #e0e9ef; border-radius: 9px; background: #f8fbfc; }
.issue-candidate-icon { display: grid; place-items: center; width: 42px; height: 42px; border-radius: 9px; color: #087d80; background: #e6f7f5; }
.issue-candidate-icon .v-icon { color: inherit; }
.issue-candidate strong, .issue-candidate span { display: block; }
.issue-candidate strong { color: #29445b; }
.issue-candidate span { margin-top: 2px; overflow: hidden; color: #718396; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.issue-return-date { display: grid; grid-template-columns: auto minmax(210px, 280px) minmax(180px, 1fr); gap: 12px; align-items: center; margin-top: 18px; padding: 14px; border-radius: 9px; background: #eef6fb; }
.issue-return-date > span { color: #637b8e; font-size: 12px; }
.search-results-inline { grid-column: 1 / -1; padding: 6px 10px; border: 1px solid #dce6ef; border-radius: 8px; background: #f8fbfd; }
.employee-details-grid { display: grid; grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr); gap: 16px; margin-top: 16px; }
.detail-card { padding: 18px; overflow: hidden; }
.detail-card-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
.detail-card-header h2 { margin: 3px 0 0; color: #1d3a53; font-size: 18px; }
.detail-count { display: grid; place-items: center; min-width: 34px; height: 34px; border-radius: 10px; color: #087d80; font-weight: 800; background: #eaf8f7; }
.assigned-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 10px; }
.assigned-item { display: flex; gap: 12px; min-width: 0; padding: 12px; border: 1px solid #e1e9f0; border-radius: 10px; background: #f9fbfd; }
.assigned-item--overdue { border-color: #f3a7af; background: #fff1f2; box-shadow: inset 3px 0 #e54858; }
.assigned-icon { display: grid; place-items: center; width: 40px; height: 40px; flex: 0 0 40px; border-radius: 9px; color: #087d80; background: #e9f8f7; }
.assigned-icon .v-icon { color: inherit; }
.assigned-item--overdue .assigned-icon { color: #d93647; background: #ffe0e4; }
.assigned-copy { display: flex; min-width: 0; flex-direction: column; }
.assigned-copy strong { overflow: hidden; color: #29445b; text-overflow: ellipsis; white-space: nowrap; }
.assigned-copy > span { color: #718396; font-size: 12px; }
.assigned-expiry { display: flex; align-items: center; gap: 4px; margin-top: 5px; color: #62788c; font-size: 11px; font-weight: 600; }
.assigned-item--overdue .assigned-expiry, .assigned-item--overdue .assigned-expiry .v-icon { color: #cf3042; }
.detail-empty { padding: 28px !important; color: #8292a2 !important; text-align: center !important; }
.detail-empty .v-icon { margin-right: 6px; color: #91a3b3; }
.discipline-score { display: flex; align-items: baseline; flex-wrap: wrap; margin-bottom: 14px; }
.discipline-score > strong { color: #173b58; font-size: 34px; line-height: 1; }
.discipline-score > span { margin-left: 3px; color: #8392a0; }
.discipline-score .v-rating { width: 100%; margin-top: 8px; }
.discipline-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.discipline-stats > div { display: flex; flex-direction: column; padding: 10px; border-radius: 9px; background: #f4f7fa; }
.discipline-stats span { color: #6c7f91; font-size: 12px; }
.discipline-stats strong { margin-top: 2px; color: #28445c; font-size: 20px; }
.discipline-stats .stat-danger { background: #fff2f3; }
.discipline-stats .stat-danger strong { color: #d83b4c; }
.history-card { grid-column: 1 / -1; padding: 0; }
.history-card .detail-card-header { padding: 18px 18px 0; }
.overdue-counter { display: inline-flex; align-items: center; gap: 5px; padding: 6px 10px; border-radius: 999px; color: #ce3142; font-size: 12px; font-weight: 700; background: #fff0f2; }
.overdue-counter .v-icon { color: inherit; }
.history-table-wrap { max-height: 520px; overflow: auto; }
.history-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; }
.history-table th { position: sticky; top: 0; z-index: 1; padding: 11px 12px; color: #667b8e; text-align: left; white-space: nowrap; background: #f5f8fb; }
.history-table td { width: auto; max-width: none; padding: 12px; border-bottom: 1px solid #e8eef3 !important; color: #3d566c; text-align: left; white-space: nowrap !important; background: #fff; }
.history-table tbody tr:hover td { background: #f9fbfd; }
.history-table .history-row--overdue td { color: #8f2632; background: #fff1f2; }
.history-table .history-row--overdue td:first-child { box-shadow: inset 4px 0 #e23b4d; }
.history-table .history-row--overdue:hover td { background: #ffe9ec; }
.history-table td:nth-child(2) strong { display: block; color: inherit; }
.history-ident { color: #7c8e9e; font-size: 11px; }
.history-term-column { min-width: 240px; }
.event-pill { display: inline-flex; padding: 4px 8px; border-radius: 6px; color: #526a7e; font-size: 11px; font-weight: 700; background: #eef2f6; }
.event-pill--issue { color: #087d66; background: #e8f8f2; }
.event-pill--return { color: #2c6ca3; background: #eaf4fc; }
.term-status { min-width: 215px; }
.term-status-label { display: flex; justify-content: space-between; gap: 8px; margin-bottom: 5px; color: #61798d; font-size: 11px; }
.term-status--overdue .term-status-label { color: #d02f40; font-weight: 700; }
.theme--dark .employee-profile, .theme--dark .kit-card, .theme--dark .sidebar-card, .theme--dark .issue-card, .theme--dark .detail-card { border-color: rgba(148, 163, 184, .18); background: #121f31; }
.theme--dark .issue-dialog-title h2 { color: #e7eef7; }
.theme--dark .issue-candidate { border-color: rgba(148, 163, 184, .18); background: #142338; }
.theme--dark .issue-candidate strong { color: #e7eef7; }
.theme--dark .issue-return-date { background: #142338; }
@media (max-width: 1050px) {
  .employee-workspace { grid-template-columns: 1fr; }
  .employee-sidebar { display: grid; grid-template-columns: repeat(3, 1fr); }
  .employee-details-grid { grid-template-columns: 1fr; }
  .history-card { grid-column: auto; }
}
@media (max-width: 720px) {
  .employee-page { padding: 8px; }
  .employee-profile { align-items: flex-start; flex-wrap: wrap; }
  .employee-number { margin-left: 0; }
  .edit-employee { margin-left: 86px; }
  .kit-toolbar { align-items: flex-start; flex-direction: column; }
  .employee-sidebar { grid-template-columns: 1fr; }
  .issue-card { grid-template-columns: 1fr; }
  .issue-return-date { grid-template-columns: auto 1fr; }
  .issue-return-date > span { grid-column: 2; }
}
</style>
